import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  Filter,
  Download,
  BarChart2,
  UserCheck
} from 'lucide-react';
import { CHART_COLORS } from '../../utils/chartTheme';
import { GroupedBarChart } from '../../utils/chartComponents';
import toast from 'react-hot-toast';
import statisticsService from '../../services/statisticsService';
import { users } from '../../services/apiService';
import { Loader } from '../UI/LoadingSpinner';
import DateRangePicker from '../UI/DateRangePicker';
import Avatar from '../UI/Avatar';
import { Autocomplete } from '@anyemp/ui-kit';
import StatisticsTable from './StatisticsTable';

const PeriodComparisonHiredStats = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [comparisonData, setComparisonData] = useState(null);
  const [filtersExpanded, setFiltersExpanded] = useState(true);

  const [period1, setPeriod1] = useState({
    startDate: '',
    endDate: ''
  });
  const [period2, setPeriod2] = useState({
    startDate: '',
    endDate: ''
  });

  const [filters, setFilters] = useState({
    manager_ids: []
  });

  const [managers, setManagers] = useState([]);

  const QUICK_PRESETS = [
    { label: 'This Month vs Last Month', value: 'month_to_month' },
    { label: 'This Quarter vs Last Quarter', value: 'quarter_to_quarter' },
    { label: 'This Year vs Last Year', value: 'year_to_year' }
  ];

  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  useEffect(() => {
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

    setPeriod1({
      startDate: formatDate(lastMonthStart),
      endDate: formatDate(lastMonthEnd)
    });
    setPeriod2({
      startDate: formatDate(thisMonthStart),
      endDate: formatDate(thisMonthEnd)
    });

    fetchManagers();
  }, []);

  const fetchManagers = async () => {
    try {
      const usersResponse = await users.getAllUsers();
      const usersArray = usersResponse.data?.users || usersResponse.users || usersResponse.data || usersResponse;
      const managersList = (Array.isArray(usersArray) ? usersArray : []).map(u => ({
        value: String(u.id),
        label: u.fullName || u.name || u.email
      }));
      setManagers(managersList);
    } catch (error) {
      console.error('Error loading managers:', error);
    }
  };

  const applyQuickPreset = (preset) => {
    const now = new Date();

    if (preset === 'month_to_month') {
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

      setPeriod1({
        startDate: formatDate(lastMonthStart),
        endDate: formatDate(lastMonthEnd)
      });
      setPeriod2({
        startDate: formatDate(thisMonthStart),
        endDate: formatDate(thisMonthEnd)
      });
    } else if (preset === 'quarter_to_quarter') {
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const thisQuarterStart = new Date(now.getFullYear(), currentQuarter * 3, 1);
      const thisQuarterEnd = new Date(now.getFullYear(), (currentQuarter + 1) * 3, 0);

      const lastQuarter = currentQuarter - 1;
      const lastQuarterYear = lastQuarter < 0 ? now.getFullYear() - 1 : now.getFullYear();
      const lastQuarterMonth = lastQuarter < 0 ? 9 : lastQuarter * 3;
      const lastQuarterStart = new Date(lastQuarterYear, lastQuarterMonth, 1);
      const lastQuarterEnd = new Date(lastQuarterYear, lastQuarterMonth + 3, 0);

      setPeriod1({
        startDate: formatDate(lastQuarterStart),
        endDate: formatDate(lastQuarterEnd)
      });
      setPeriod2({
        startDate: formatDate(thisQuarterStart),
        endDate: formatDate(thisQuarterEnd)
      });
    } else if (preset === 'year_to_year') {
      const thisYearStart = new Date(now.getFullYear(), 0, 1);
      const thisYearEnd = new Date(now.getFullYear(), 11, 31);
      const lastYearStart = new Date(now.getFullYear() - 1, 0, 1);
      const lastYearEnd = new Date(now.getFullYear() - 1, 11, 31);

      setPeriod1({
        startDate: formatDate(lastYearStart),
        endDate: formatDate(lastYearEnd)
      });
      setPeriod2({
        startDate: formatDate(thisYearStart),
        endDate: formatDate(thisYearEnd)
      });
    }
  };

  const runComparison = async () => {
    if (!period1.startDate || !period1.endDate || !period2.startDate || !period2.endDate) {
      toast.error('Please select both periods');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await statisticsService.getPeriodComparisonHired({
        period1_start: period1.startDate,
        period1_end: period1.endDate,
        period2_start: period2.startDate,
        period2_end: period2.endDate,
        manager_ids: filters.manager_ids.length ? filters.manager_ids : undefined
      });

      const data = response.data || response;
      console.log('Period Comparison Hired Response:', data);
      setComparisonData(data);
    } catch (err) {
      console.error('Error running comparison:', err);
      setError(err.message);
      toast.error('Failed to run comparison');
    } finally {
      setLoading(false);
    }
  };

  const renderChangeIndicator = (change) => {
    const { absolute, percentage } = change;
    const isPositive = absolute > 0;
    const isNegative = absolute < 0;

    const IconComponent = isPositive ? TrendingUp : isNegative ? TrendingDown : Minus;
    const colorClass = isPositive
      ? 'text-green-600 dark:text-green-400'
      : isNegative
      ? 'text-red-600 dark:text-red-400'
      : 'text-gray-500 dark:text-gray-400';

    return (
      <div className={`flex items-center gap-1 ${colorClass}`}>
        <IconComponent className="h-4 w-4" />
        <span className="font-semibold">
          {absolute >= 0 ? '+' : ''}
          {absolute}
        </span>
        <span className="text-sm">
          ({percentage >= 0 ? '+' : ''}
          {percentage}%)
        </span>
      </div>
    );
  };

  const renderSummaryCards = () => {
    if (!comparisonData) return null;

    const { period1, period2, comparison } = comparisonData;

    const cards = [
      {
        label: 'Total Hired',
        period1Value: period1.total_hired,
        period2Value: period2.total_hired,
        change: comparison.total_hired
      }
    ];

    return (
      <div className="space-y-6 mb-6">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            Hired Counts
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {cards.map((card, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
              >
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
                  {card.label}
                </h3>
                <div className="flex items-baseline justify-between mb-2">
                  <span className="break-words text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {card.period2Value}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    vs {card.period1Value}
                  </span>
                </div>
                {renderChangeIndicator(card.change)}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderComparisonChart = () => {
    if (!comparisonData) return null;

    const { period1, period2 } = comparisonData;

    const chartData = [
      {
        name: 'Total Hired',
        [period1.label]: period1.total_hired || 0,
        [period2.label]: period2.total_hired || 0
      }
    ];

    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Hired Comparison
        </h3>
        <GroupedBarChart
          data={chartData}
          bars={[
            { key: period1.label, name: period1.label, color: CHART_COLORS[0] },
            { key: period2.label, name: period2.label, color: CHART_COLORS[1] }
          ]}
        />
      </div>
    );
  };

  const rowKeyManager = useCallback(
    (item) => item.manager?.id || item.manager?.email || JSON.stringify(item),
    []
  );

  const tableColumns = useMemo(() => {
    if (!comparisonData) return [];

    const { period1, period2 } = comparisonData;

    return [
      {
        label: 'Manager',
        key: 'manager',
        sortable: true,
        render: (item) => {
          const manager = item.manager;
          if (!manager) {
            return <span className="text-gray-400 dark:text-gray-500">Unassigned</span>;
          }
          return (
            <div className="flex flex-wrap items-center gap-2">
              <Avatar
                avatar={manager.avatar}
                name={manager.fullName || manager.name || manager.email}
                size="sm"
              />
              <span className="text-gray-900 dark:text-gray-100">{manager.fullName || manager.name || manager.email}</span>
            </div>
          );
        }
      },
      {
        label: period1.label,
        key: 'period1_total',
        sortable: true,
        render: (item) => {
          const period1Manager = period1.by_manager?.find(
            (m) => m.manager_id === item.manager_id
          );
          return <span className="font-medium text-gray-900 dark:text-gray-100">{period1Manager?.total || 0}</span>;
        }
      },
      {
        label: period2.label,
        key: 'period2_total',
        sortable: true,
        render: (item) => {
          const period2Manager = period2.by_manager?.find(
            (m) => m.manager_id === item.manager_id
          );
          return <span className="font-medium text-gray-900 dark:text-gray-100">{period2Manager?.total || 0}</span>;
        }
      },
      {
        label: 'Change',
        key: 'change',
        sortable: true,
        sortValue: (item) => item.total.absolute,
        render: (item) => renderChangeIndicator(item.total)
      }
    ];
  }, [comparisonData]);

  const handleExport = () => {
    if (!comparisonData) return;

    const { period1, period2, comparison } = comparisonData;
    const BOM = '\uFEFF';

    const headers = ['Manager', period1.label, period2.label, 'Change (Absolute)', 'Change (%)'];

    const rows = comparison.by_manager.map(item => {
      const manager = item.manager;
      const managerName = manager ? (manager.fullName || manager.name || manager.email) : 'Unassigned';

      const period1Manager = period1.by_manager?.find(m => m.manager_id === item.manager_id);
      const period2Manager = period2.by_manager?.find(m => m.manager_id === item.manager_id);

      return [
        managerName,
        period1Manager?.total || 0,
        period2Manager?.total || 0,
        item.total.absolute,
        `${item.total.percentage}%`
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `period-comparison-hired-${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <UserCheck className="h-6 w-6 text-primary-600" />
            <h1 className="break-words text-2xl font-bold text-gray-900 dark:text-gray-100">
              Period Comparison - Hired
            </h1>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Compare hired counts across two time periods
          </p>
        </div>

        {comparisonData && (
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors"
          >
            <Download className="h-4 w-4" />
            <span>Export CSV</span>
          </button>
        )}
      </div>

      {/* Period Selection Panel */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        {/* Quick Presets */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Quick Presets
          </label>
          <div className="flex flex-wrap gap-2">
            {QUICK_PRESETS.map((preset) => (
              <button
                key={preset.value}
                onClick={() => applyQuickPreset(preset.value)}
                className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        {/* Date Range Pickers */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
          <DateRangePicker
            label="Period 1"
            startDate={period1.startDate}
            endDate={period1.endDate}
            onChange={(range) => setPeriod1(range)}
          />
          <DateRangePicker
            label="Period 2"
            startDate={period2.startDate}
            endDate={period2.endDate}
            onChange={(range) => setPeriod2(range)}
          />
        </div>

        {/* Filters toggle */}
        <button
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4 transition-colors"
        >
          <Filter className="h-4 w-4" />
          <span>{filtersExpanded ? 'Hide' : 'Show'} Filters</span>
        </button>

        {/* Filters */}
        {filtersExpanded && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Managers
              </label>
              <Autocomplete
                multiple
                clearable
                fullWidth
                options={managers}
                value={filters.manager_ids}
                onChange={(val) => setFilters((prev) => ({ ...prev, manager_ids: val || [] }))}
                placeholder="All managers"
              />
            </div>
          </div>
        )}

        {/* Run button */}
        <div className="flex justify-end">
          <button
            onClick={runComparison}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            <BarChart2 className="h-4 w-4" />
            {loading ? 'Running...' : 'Compare Periods'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Results */}
      {comparisonData && (
        <>
          {renderSummaryCards()}
          {renderComparisonChart()}

          {/* Managers Comparison Table */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Comparison by Manager
            </h3>
            <StatisticsTable
              data={comparisonData.comparison.by_manager}
              columns={tableColumns}
              isLoading={loading}
              rowKey={rowKeyManager}
            />
          </div>
        </>
      )}

      {/* Loading — only on initial run, before any results */}
      {loading && !comparisonData && (
        <div className="flex justify-center items-center min-h-[200px]">
          <Loader variant="component" size="md" />
        </div>
      )}

      {/* Initial state */}
      {!comparisonData && !loading && !error && (
        <div className="flex flex-col items-center justify-center min-h-[300px] text-gray-400 dark:text-gray-500">
          <Calendar className="h-12 w-12 mb-3 opacity-50" />
          <p className="text-lg font-medium">Select periods and run comparison</p>
          <p className="text-sm mt-1">
            Choose two time periods to compare hired statistics
          </p>
        </div>
      )}
    </div>
  );
};

export default PeriodComparisonHiredStats;
